import { Link } from "react-router-dom";
import { Radio, MapPin, Camera, ArrowUpRight } from "lucide-react";
import { Reveal, MaskLines } from "../components/Reveal";
import { FILMS, STATUSES } from "../data/films";

export default function OnTheFloor() {
  const films = FILMS.filter((f) => f.status === STATUSES.ON_THE_FLOOR);

  return (
    <main data-testid="on-the-floor-page" className="bg-black pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-red)] mb-6 flex items-center gap-3">
          <Radio size={12} className="animate-pulse" />
          Now Filming · On Set
        </p>
        <h1 className="font-serif-display font-light text-white text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tighter">
          <MaskLines lines={["On the", "floor."]} />
        </h1>
        <Reveal delay={1} className="mt-10 max-w-xl">
          <p className="text-white/60 leading-relaxed">
            Two productions currently on set — dispatches from the clapperboard,
            location logs and behind-the-scenes windows into the making.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-28 space-y-16">
        {films.map((film, i) => (
          <Reveal key={film.slug} delay={i}
            className="relative border border-white/10 bg-[color:var(--dk-muted)] overflow-hidden group"
            data-testid={`floor-${film.slug}`}
          >
            {/* Clapperboard stripe */}
            <div className="absolute top-0 left-0 right-0 h-2 z-10 flex">
              {Array.from({ length: 24 }).map((_, k) => (
                <div key={k} className={`flex-1 ${k % 2 === 0 ? "bg-white" : "bg-black"}`} />
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12">
              {/* Poster / BTS placeholder */}
              <div className="md:col-span-7 relative min-h-[340px] md:min-h-[480px] overflow-hidden">
                <img src={film.poster} alt={film.title} className="film-hover-img absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

                {/* REC indicator */}
                <div className="absolute top-8 left-8 flex items-center gap-3 bg-black/70 backdrop-blur px-4 py-2 border border-[color:var(--dk-red)]/60">
                  <span className="w-2.5 h-2.5 rounded-full bg-[color:var(--dk-red)] animate-pulse" />
                  <span className="text-[10px] uppercase tracking-overline text-white">REC · Now Filming</span>
                </div>

                {/* Location tag */}
                <div className="absolute bottom-6 left-6 flex items-center gap-2 text-[10px] uppercase tracking-overline text-white/80">
                  <MapPin size={12} className="text-[color:var(--dk-gold)]" />
                  {film.location}
                </div>

                {/* Slate — bottom right */}
                <div className="absolute bottom-6 right-6 border border-white/30 bg-black/70 backdrop-blur px-4 py-3 font-serif-display text-white text-xs">
                  <div className="flex gap-4">
                    <span><span className="text-[color:var(--dk-gold)]/70">Scene</span> {String(i + 1).padStart(2, "0")}</span>
                    <span><span className="text-[color:var(--dk-gold)]/70">Take</span> 03</span>
                  </div>
                </div>
              </div>

              <div className="md:col-span-5 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-4">{film.genre}</p>
                  <h2 className="font-serif-display text-white text-3xl md:text-5xl leading-tight tracking-tight">{film.title}</h2>
                  <p className="mt-3 text-white/50 font-serif-display italic text-sm">
                    {film.nepaliTitle} · {film.hindiTitle}
                  </p>
                  <p className="mt-6 text-white/70 leading-relaxed">{film.shortSynopsis}</p>
                </div>

                {/* Production diary & BTS placeholders */}
                <div className="mt-8 grid grid-cols-2 gap-3">
                  <div className="border border-white/10 p-4">
                    <p className="text-[9px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2 flex items-center gap-2">
                      <Camera size={10} /> Production Diary
                    </p>
                    <p className="text-xs text-white/40 italic">Coming soon — daily on-set updates</p>
                  </div>
                  <div className="border border-white/10 p-4">
                    <p className="text-[9px] uppercase tracking-overline text-[color:var(--dk-gold)] mb-2 flex items-center gap-2">
                      <Camera size={10} /> Behind-the-Scenes
                    </p>
                    <p className="text-xs text-white/40 italic">Gallery coming soon</p>
                  </div>
                </div>

                <Link to={`/films/${film.slug}`}
                  data-testid={`floor-link-${film.slug}`}
                  className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-overline text-white eyebrow-link w-fit">
                  View project <ArrowUpRight size={14} />
                </Link>
              </div>
            </div>

            {/* Clapperboard stripe bottom */}
            <div className="absolute bottom-0 left-0 right-0 h-2 z-10 flex">
              {Array.from({ length: 24 }).map((_, k) => (
                <div key={k} className={`flex-1 ${k % 2 === 0 ? "bg-black" : "bg-white"}`} />
              ))}
            </div>
          </Reveal>
        ))}
      </section>
    </main>
  );
}
